import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BarChart3, Users, TrendingUp, Smartphone, Monitor, Globe, RefreshCw, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOURCE_STYLES = {
  Facebook: { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500" },
  Instagram: { bg: "bg-pink-100", text: "text-pink-700", dot: "bg-pink-500" },
  Google: { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500" },
  "Google Ads": { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500" },
  WhatsApp: { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500" },
  LinkedIn: { bg: "bg-sky-100", text: "text-sky-700", dot: "bg-sky-500" },
  YouTube: { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-600" },
  "Twitter/X": { bg: "bg-slate-100", text: "text-slate-800", dot: "bg-slate-800" },
  TikTok: { bg: "bg-purple-100", text: "text-purple-700", dot: "bg-purple-500" },
  Direct: { bg: "bg-orange-100", text: "text-orange-700", dot: "bg-orange-500" },
};

const sourceStyle = (s) => SOURCE_STYLES[s] || { bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400" };

export default function AnalyticsPanel({ password }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics`, {
        headers: { "X-Admin-Password": password },
      });
      setData(res.data);
    } catch (err) {
      toast.error("加载失败");
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="py-20 text-center text-slate-400">
        <Loader2 size={24} className="mx-auto animate-spin" />
        <p className="mt-4 text-sm">加载中...</p>
      </div>
    );
  }

  if (!data) return <div className="py-20 text-center text-slate-400">暂无数据</div>;

  const maxDaily = Math.max(1, ...data.daily_series.map((d) => d.views));
  const totalSourceCount = data.sources.reduce((a, s) => a + s.count, 0) || 1;

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-serif-sc text-3xl lg:text-4xl font-bold text-slate-900">流量分析</h1>
          <p className="text-slate-500 mt-2 text-sm">追踪访问来源、设备与趋势</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="analytics-refresh-button"
          className="text-slate-700 hover:text-orange-600"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          <span className="ml-2 text-xs">刷新</span>
        </Button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard label="Total Views" value={data.total} icon={Users} />
        <StatCard label="Today" value={data.today} icon={TrendingUp} accent="text-orange-600" />
        <StatCard label="Yesterday" value={data.yesterday} icon={BarChart3} />
        <StatCard label="Last 7 Days" value={data.last_7_days} icon={BarChart3} accent="text-sky-600" />
        <StatCard label="Last 30 Days" value={data.last_30_days} icon={BarChart3} accent="text-emerald-600" />
      </div>

      {/* Traffic sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe size={16} className="text-slate-500" />
            <h2 className="font-serif-sc text-lg font-semibold text-slate-900">流量来源</h2>
          </div>
          {data.sources.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">暂无数据</p>
          ) : (
            <div className="space-y-3">
              {data.sources.map((s) => {
                const style = sourceStyle(s.source);
                const pct = ((s.count / totalSourceCount) * 100).toFixed(1);
                return (
                  <div key={s.source} data-testid={`source-${s.source}`}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                        <span className={`font-medium ${style.text}`}>{s.source}</span>
                      </div>
                      <span className="text-slate-500 tabular-nums font-display">
                        <span className="text-slate-900 font-medium">{s.count}</span>
                        <span className="ml-2 text-xs">{pct}%</span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${style.dot}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Devices */}
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Smartphone size={16} className="text-slate-500" />
            <h2 className="font-serif-sc text-lg font-semibold text-slate-900">设备分布</h2>
          </div>
          {data.devices.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">暂无数据</p>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {["mobile", "desktop", "tablet"].map((k) => {
                const d = data.devices.find((x) => x.device === k);
                const count = d?.count || 0;
                const total = data.devices.reduce((a, x) => a + x.count, 0) || 1;
                const pct = ((count / total) * 100).toFixed(0);
                const Icon = k === "mobile" ? Smartphone : Monitor;
                const labelMap = { mobile: "手机", desktop: "电脑", tablet: "平板" };
                return (
                  <div key={k} className="bg-slate-50 rounded-sm p-4 text-center">
                    <Icon size={20} className="mx-auto text-slate-400" />
                    <div className="font-display font-bold text-2xl mt-2 tabular-nums">{count}</div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-display mt-1">
                      {labelMap[k]} · {pct}%
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 14-day chart */}
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <div className="flex items-center gap-2 mb-6">
          <BarChart3 size={16} className="text-slate-500" />
          <h2 className="font-serif-sc text-lg font-semibold text-slate-900">近 14 天访问趋势</h2>
        </div>
        <div className="flex items-end gap-2 h-40" data-testid="daily-chart">
          {data.daily_series.map((d) => {
            const height = (d.views / maxDaily) * 100;
            const isToday = d.date === new Date().toISOString().slice(0, 10);
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-2 group">
                <div className="text-[10px] font-display text-slate-400 tabular-nums opacity-0 group-hover:opacity-100 transition-opacity">
                  {d.views}
                </div>
                <div
                  className={`w-full rounded-t-sm transition-all ${isToday ? "bg-orange-600" : "bg-sky-400"} hover:opacity-80 relative`}
                  style={{ height: `${Math.max(height, 2)}%`, minHeight: "2px" }}
                  title={`${d.date}: ${d.views} 次`}
                />
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-2 text-[10px] font-display text-slate-400 tabular-nums">
          {data.daily_series.map((d, i) => (
            <span key={d.date} className={i % 2 === 0 ? "" : "invisible"}>
              {d.date.slice(5)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent = "text-slate-900" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-display">{label}</span>
        <Icon size={16} className="text-slate-400" />
      </div>
      <div className={`font-display font-bold text-3xl mt-3 tracking-tighter tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}
